import { C } from '../../lib/landingColors'
import {
  MarketingHeading,
  MarketingSubheading,
  MarketingParagraph,
  MarketingList,
  MarketingListItem,
  MarketingLink,
} from '../../components/MarketingShell'

/**
 * The English Privacy Policy — the reference version (section 15).
 *
 * Lifted out of `pages/PrivacyPolicyPage.tsx` unchanged on 2026-09-09 when the
 * page became a pair; the only edit to the text is the new section 15. A
 * privacy policy is the document a DPA negotiation quotes, so nothing else
 * moved.
 *
 * Note for the next person editing section 7: the third-party processor list
 * here is a SUMMARY. The authoritative, per-processor list with regions and
 * verification state is /legal/subprocessors, driven by
 * `content/subprocessors.ts`. If a processor changes, that page is the one that
 * has to be right first — and the German sibling of THIS page has to be updated
 * in the same commit, which `legalTranslationParity.test.tsx` enforces
 * structurally.
 */
export function PrivacyBodyEn() {
  return (
    <>
      <section>
        <MarketingHeading>1. Data Controller</MarketingHeading>
        <MarketingParagraph>
          The data controller is the studio owner operating through the StudioFlow
          platform. For data protection inquiries, please contact the organization
          administrator via the email address configured in the organization's settings.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>2. What Data We Collect</MarketingHeading>
        <MarketingSubheading>Account Data</MarketingSubheading>
        <MarketingParagraph>Name, email address, and phone number of team members who use the platform.</MarketingParagraph>

        <MarketingSubheading>Client Data</MarketingSubheading>
        <MarketingParagraph>
          Client names, email addresses, phone numbers, project details, and communication
          history — entered by the studio team to manage their business.
        </MarketingParagraph>

        <MarketingSubheading>Photo &amp; File Data</MarketingSubheading>
        <MarketingParagraph>
          Images uploaded to galleries, including EXIF metadata (camera model, date taken,
          GPS coordinates if present), file metadata (size, dimensions, format), and
          associated admin/client notes.
        </MarketingParagraph>

        <MarketingSubheading>Gallery Visitor Data</MarketingSubheading>
        <MarketingParagraph>
          When gallery visitors interact with public galleries: email address (if entered
          for favorites functionality), a client identification token, and IP address
          (recorded for contract and offer acceptance timestamps).
        </MarketingParagraph>

        <MarketingSubheading>Technical Data</MarketingSubheading>
        <MarketingParagraph>
          Browser user agent (for push notification subscriptions), device type, and
          standard web server access logs.
        </MarketingParagraph>

        <MarketingSubheading>Usage Data</MarketingSubheading>
        <MarketingParagraph>Activity logs within the platform for audit and security purposes.</MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>3. Legal Basis (GDPR Art. 6)</MarketingHeading>
        <MarketingList>
          <MarketingListItem>
            <strong>Legitimate interest (Art. 6(1)(f)):</strong> Processing client data
            to operate and manage the photography business.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Contract performance (Art. 6(1)(b)):</strong> Providing the service
            to registered team members.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Consent (Art. 6(1)(a)):</strong> Gallery visitors who voluntarily
            enter their email address for the favorites feature.
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>4. Data Processing Purposes</MarketingHeading>
        <MarketingParagraph>We process personal data for the following purposes:</MarketingParagraph>
        <MarketingList>
          <MarketingListItem>Studio and project management</MarketingListItem>
          <MarketingListItem>Client scheduling and communication</MarketingListItem>
          <MarketingListItem>Invoicing, offer, and contract generation</MarketingListItem>
          <MarketingListItem>Photo gallery delivery and client review</MarketingListItem>
          <MarketingListItem>Team collaboration and task management</MarketingListItem>
          <MarketingListItem>Calendar synchronization</MarketingListItem>
          <MarketingListItem>Platform security and abuse prevention</MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>5. Data Storage &amp; Location</MarketingHeading>
        <MarketingList>
          <MarketingListItem>
            <strong>Application database:</strong> Hetzner VPS, Germany (EU) — encrypted
            in transit via TLS.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Photo &amp; video storage:</strong> Backblaze B2 object storage,
            EU region (eu-central) — encrypted at rest and in transit.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Content delivery:</strong> Cloudflare CDN serves cached
            photo and video assets from edge locations. Cloudflare acts as a
            pass-through caching layer; it does not persistently store your
            content beyond standard CDN cache TTLs.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Backups:</strong> Server filesystem on the same Hetzner VPS (Germany, EU) —
            daily XLSX exports of the application database with 30-day rolling retention.
            Backup files never leave the EU. Photo and video master files are not
            included in the application backup; see the Terms &amp; Conditions, Section 5.5.
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>6. Data Retention</MarketingHeading>
        <MarketingList>
          <MarketingListItem>
            <strong>Active data:</strong> Retained for the duration of the organization's
            existence on the platform.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Soft-deleted data:</strong> Retained for up to 30 days to allow recovery,
            then permanently removed.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Backups:</strong> Daily backups retained for 30 days; monthly archives
            for 2 years.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Audit logs:</strong> Retained for 12 months.
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>7. Third-Party Processors</MarketingHeading>
        <MarketingList>
          <MarketingListItem><strong>Hetzner Online GmbH</strong> — Server hosting (Germany, EU)</MarketingListItem>
          <MarketingListItem><strong>Backblaze, Inc.</strong> — Photo and video object storage (B2, EU region)</MarketingListItem>
          <MarketingListItem>
            <strong>Cloudflare, Inc.</strong> — Content delivery network (CDN) for
            cached photo/video delivery; also provides DNS and TLS termination at
            the edge. No persistent storage of customer content.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Google LLC</strong> — Calendar synchronization only
            (via the organization owner's own Google account; no backup data is sent to Google)
          </MarketingListItem>
          <MarketingListItem>
            <strong>Web Push services</strong> — Browser push notification delivery
            (via browser vendor endpoints)
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>8. Your Rights</MarketingHeading>
        <MarketingParagraph>
          Under the EU General Data Protection Regulation (GDPR) and the Swiss Federal
          Act on Data Protection (nDSG / FADP), you have the following rights:
        </MarketingParagraph>
        <MarketingList>
          <MarketingListItem><strong>Right of access (Art. 15 GDPR):</strong> Request a copy of your personal data.</MarketingListItem>
          <MarketingListItem><strong>Right to rectification (Art. 16):</strong> Request correction of inaccurate data.</MarketingListItem>
          <MarketingListItem>
            <strong>Right to erasure (Art. 17):</strong> Request deletion of your data.
            Organization owners can delete their entire organization via the "Delete
            Organization" feature in Settings.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Right to data portability (Art. 20):</strong> Export your data in a
            structured format (XLSX backup export is available).
          </MarketingListItem>
          <MarketingListItem><strong>Right to object (Art. 21):</strong> Object to processing based on legitimate interest.</MarketingListItem>
          <MarketingListItem><strong>Right to withdraw consent:</strong> Where processing is based on consent, you may withdraw it at any time.</MarketingListItem>
          <MarketingListItem>
            <strong>Right to lodge a complaint:</strong> With the Federal Data Protection
            and Information Commissioner (FDPIC) in Switzerland, or the relevant
            supervisory authority in your EU member state.
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>9. Cookies &amp; Local Storage</MarketingHeading>
        <MarketingParagraph>
          StudioFlow does not use tracking cookies, analytics scripts, or
          third-party advertising. We use browser localStorage solely for:
        </MarketingParagraph>
        <MarketingList>
          <MarketingListItem>Authentication tokens (JWT) for maintaining your login session</MarketingListItem>
          <MarketingListItem>Client identification tokens for gallery favorites</MarketingListItem>
          <MarketingListItem>UI preferences (theme, sidebar state)</MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>10. Data Breach Notification</MarketingHeading>
        <MarketingParagraph>
          In accordance with GDPR Art. 33/34 and Swiss FADP Art. 24, we will notify
          the relevant supervisory authority within 72 hours of becoming aware of a
          personal data breach that is likely to result in a risk to your rights and
          freedoms. Affected individuals will be notified without undue delay when the
          breach is likely to result in a high risk.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>11. Children</MarketingHeading>
        <MarketingParagraph>
          StudioFlow is not directed at individuals under the age of 16.
          We do not knowingly collect personal data from children.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>12. Swiss Federal Act on Data Protection (nDSG / FADP)</MarketingHeading>
        <MarketingParagraph>
          As the platform is operated from Zürich, Switzerland, the Swiss Federal Act
          on Data Protection (nDSG, in force since 1 September 2023) applies in addition
          to the GDPR. This means:
        </MarketingParagraph>
        <MarketingList>
          <MarketingListItem>We maintain a register of processing activities as required by Art. 12 nDSG.</MarketingListItem>
          <MarketingListItem>Cross-border data transfers comply with Art. 16-17 nDSG (all data remains in the EU/Switzerland).</MarketingListItem>
          <MarketingListItem>Data breach notifications follow the requirements of Art. 24 nDSG.</MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>13. Changes to This Policy</MarketingHeading>
        <MarketingParagraph>
          We may update this Privacy Policy from time to time. For material changes,
          we will provide at least 30 days' notice via email to the organization owner.
          The updated policy will be posted on this page with a revised "Last updated" date.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>14. Contact</MarketingHeading>
        <MarketingParagraph>
          For data protection inquiries, requests to exercise your rights, or any
          questions about this Privacy Policy, please contact the organization
          administrator at the email address listed in the platform's Settings page.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>15. Language</MarketingHeading>
        <MarketingParagraph>
          This Privacy Policy is published in English and in German. The
          English version is the reference version; the German version is
          provided for convenience only. In the event of any conflict,
          inconsistency, or difference of interpretation between the two, the
          English version prevails. The German version is available at{' '}
          <MarketingLink to="/de/privacy">/de/privacy</MarketingLink>.
        </MarketingParagraph>
      </section>

      <div className="mt-12 pt-6 text-sm" style={{ borderTop: `1px solid ${C.border}` }}>
        <MarketingLink to="/terms">Terms &amp; Conditions</MarketingLink>
      </div>
    </>
  )
}
